import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectIntegrationId } from '@/redux/session/selectors';
import { InfobipRcsConfig, InfobipRcsIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl } from '@/util/constants';

import { DeleteFieldset } from '../Components/DeleteFieldset';
import IntegrationConfigWrapper from '../Components/Integration';
import { baseUrlSchema } from '../InfobipCommon/baseUrlSchema';
import { ConfigurationOverviewCard } from '../InfobipCommon/ConfigurationOverviewCard';
import { ConnectCard } from '../InfobipCommon/ConnectCard';

const IntegrationInfobipRcs = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const integrationId = useSelector(selectIntegrationId);
  const { integration } = useIntegrations<InfobipRcsIntegration>(deskId, integrationId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  if (!integration) {
    return null;
  }

  const config: InfobipRcsConfig = integration.config ?? {};
  const isConnected = Boolean(
    config.apikey && config.base_url && config.sender_name && config.webhook_secret
  );

  const connectFields = [
    {
      name: 'apikey',
      label: t('integrations.infobip_rcs_connect.apikey.title'),
      placeholder: t('integrations.infobip_rcs_connect.apikey.placeholder'),
      value: config.apikey ?? '',
    },
    {
      name: 'base_url',
      label: t('integrations.infobip_rcs_connect.base_url.title'),
      placeholder: t('integrations.infobip_rcs_connect.base_url.placeholder'),
      value: config.base_url ?? '',
      validate: baseUrlSchema,
    },
    {
      name: 'sender_name',
      label: t('integrations.infobip_rcs_connect.sender_name.title'),
      placeholder: t('integrations.infobip_rcs_connect.sender_name.placeholder'),
      value: config.sender_name ?? '',
    },
  ];

  const overviewFields = [
    { label: t('integrations.infobip_rcs_info.apikey'), value: config.apikey },
    { label: t('integrations.infobip_rcs_info.base_url'), value: config.base_url },
    { label: t('integrations.infobip_rcs_info.sender_name'), value: config.sender_name },
    { label: t('integrations.infobip_rcs_info.webhook_secret'), value: config.webhook_secret },
  ];

  if (!isConnected) {
    return (
      <div className="flex w-xl flex-col gap-4">
        <fieldset disabled={!canWrite} className="contents">
          <ConnectCard
            integration={integration}
            title={t('integrations.infobip_rcs_connect.title')}
            docsUrl={getDocsUrl('/docs/integrations/infobip-rcs')}
            fields={connectFields}
          />
        </fieldset>
        <div className="flex justify-start">
          <DeleteFieldset integration={integration} />
        </div>
      </div>
    );
  }

  return (
    <IntegrationConfigWrapper integration={integration}>
      <ConfigurationOverviewCard
        integration={integration}
        title={t('integrations.infobip_rcs_info.overview')}
        fields={overviewFields}
      />
    </IntegrationConfigWrapper>
  );
};

export default IntegrationInfobipRcs;
